import { Popover } from '@mui/material';
import { useState } from 'react';
import { useSWRConfig } from 'swr';
import Button from '../../../common/Button/Button';
import { RightArrow } from '../../../../lib/icons/RightArrow';
import styles from './ScormDownloadButton.module.scss';
import { classes } from '../../../../lib/utils/cssUtils';
import { useId } from '../../../../lib/hooks/useId';
import {
  DownloadOption,
  DownloadsListProps,
  FormatDownloadOption,
} from '../../compound/DownloadsList/DownloadsList.types';
import PureLink from '../../../common/Link/PureLink';
import { Label } from '../../../common/Label/Label';
import { DOWNLOADS_LIST_LABELS } from '../../../../lib/constants/labelKeys';
import WaiverDialog from '../WaiverDialog/WaiverDialog';
import {
  useDownloadListContent,
  useTermsAndConditionStatus,
  useTermsAndConditionStore,
} from '../../../../lib/api/scormApi';
import { useViewContext } from '../../../../lib/context/ViewContext';
import { useSnackbarContext } from '../../../../lib/context/SnackbarContext';
import { NotificationType } from '../Notification/Notification';
import { useScorm } from '../../../../lib/context/ScormContext';
import { downloadData } from '../../../../lib/utils/downloadData';
import Loader from '../../../common/Loader/Loader';
import { replaceAll } from '../../../../lib/utils/functional';
import { Api } from '../../../../lib/types/apiSpec';
import {
  pushDownloadEvent,
  pushWaiverEvent,
  WaiverEventActions,
} from '../../../../lib/utils/analytics/analyticsScormHelper';
import useErrorMapping from '../../../../lib/hooks/useErrorMapping';

const isFormatOption = (option: DownloadOption): option is FormatDownloadOption => option.optionType === 'format';

type ScormDownloadButtonProps = Pick<
  DownloadsListProps,
  'downloadOptionsItems' | 'chooseFormatLabel' | 'buttonLabel' | 'waiverContent' | 'contentDownloadSuccessMessage'
>;

const ScormDownloadButton = ({
  downloadOptionsItems,
  chooseFormatLabel,
  buttonLabel,
  waiverContent,
  contentDownloadSuccessMessage,
}: ScormDownloadButtonProps) => {
  const id = useId('popover-');
  const view = useViewContext();
  const ean = view.app.user?.mainFacilityEan ?? '';
  const userid = view.app.user?.userId ?? '';
  const { downloadsList } = useScorm();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [actionPending, setActionPending] = useState(false);
  const [onWaiverAcceptedFormat, setOnWaiverAcceptedFormat] = useState<Api.ScormLearningStandard | null>(null);
  const checkTermsAndConditionsStatus = useTermsAndConditionStatus();
  const storeTermsAndConditionsStatus = useTermsAndConditionStore();
  const downloadContent = useDownloadListContent();
  const { triggerSnackbar } = useSnackbarContext();
  const { mutate } = useSWRConfig();
  const { triggerSnackbarError } = useErrorMapping();

  const downloadProceed = async (format: Api.ScormLearningStandard) => {
    try {
      const response = await downloadContent({
        type: format,
        ean,
        userid,
      });
      pushDownloadEvent(format);
      const filename: string | undefined = response?.headers['content-disposition']?.split('filename=')?.[1];

      downloadData(response?.data || '', replaceAll('"', '')(filename ?? ''), { contentType: 'application/zip' }, true);
      triggerSnackbar(contentDownloadSuccessMessage, NotificationType.Success);

      downloadsList.mutate();
      mutate(([key]: string[]) => {
        return key.match(`/facilities/${ean}/downloads/history`);
      });
    } catch (error: any) {
      triggerSnackbarError(error);
    } finally {
      setActionPending(false);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChooseDropdownOption = async (format: Api.ScormLearningStandard) => {
    setAnchorEl(null);
    setActionPending(true);

    try {
      const response = await checkTermsAndConditionsStatus({
        userid,
        ean,
      });

      if (!response?.accepted) {
        setOnWaiverAcceptedFormat(format);

        return;
      }

      await downloadProceed(format);
    } catch (error: any) {
      triggerSnackbarError(error);
    }
  };

  const handleWaiverConfirm = async (format: Api.ScormLearningStandard) => {
    try {
      setOnWaiverAcceptedFormat(null);
      await storeTermsAndConditionsStatus({
        userid,
        ean,
        accept: true,
      });
      pushWaiverEvent(WaiverEventActions.Accepted);

      await downloadProceed(format);
    } catch (error: any) {
      triggerSnackbarError(error);
    }
  };

  const handleWaiverReject = () => {
    setOnWaiverAcceptedFormat(null);
    setActionPending(false);
    pushWaiverEvent(WaiverEventActions.Cancel);
  };

  return (
    <div>
      {actionPending && <Loader containerProps={{ className: styles.loader }} />}
      <Button
        variant="contained"
        onClick={event => setAnchorEl(event.currentTarget)}
        aria-haspopup="true"
        aria-expanded={!!anchorEl}
        aria-controls={id}
        className={classes(styles.button, { [styles.hidden]: actionPending })}
        disabled={!ean || !userid}
      >
        {buttonLabel} <RightArrow className={classes(styles.arrow, styles.arrowClosed)} />
      </Button>
      <Popover
        id={id}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div className={styles.popoverContent}>
          <div className={styles.popoverTitle}>
            {chooseFormatLabel} <RightArrow className={classes(styles.arrow, styles.arrowOpened)} />
          </div>
          <ul className={styles.popoverListContainer}>
            {downloadOptionsItems?.map(option => (
              <li key={option.uniqueId}>
                {isFormatOption(option) ? (
                  <Button
                    variant="outlined"
                    className={styles.popoverOptionButton}
                    onClick={() => handleChooseDropdownOption(option.downloadFormat)}
                  >
                    <Label name={DOWNLOADS_LIST_LABELS.formatLabel(option.downloadFormat)} />
                  </Button>
                ) : (
                  <PureLink href={option.link} className={styles.popoverOptionLink}>
                    {option.label}
                  </PureLink>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Popover>
      {onWaiverAcceptedFormat && (
        <WaiverDialog
          waiverContent={waiverContent}
          onConfirm={() => handleWaiverConfirm(onWaiverAcceptedFormat)}
          onReject={handleWaiverReject}
        />
      )}
    </div>
  );
};

export default ScormDownloadButton;
